import React, { useState } from 'react';
import { HelpCircle, Mic, Sparkles, Upload, Heart, MoreHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import ButtonCircle from '@/components/ui/ButtonCircle';
import { useIsMobile } from '@/hooks/use-mobile';
import CustomDrawer from '@/components/ui/CustomDrawer';
import { APP_URL } from '@/lib/constants';

interface ActionButton {
  text: string;
  variant?: 'default' | 'destructive' | 'outline';
  onClick: () => void;
  disabled?: boolean;
}

interface TimelineSidebarBottomNavProps {
  mode: 'create' | 'view';
  onMic?: () => void;
  onSparkles?: () => void;
  onShare?: () => void;
  onHeart?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
  entryTitle?: string;
}

const TimelineSidebarBottomNav = ({
  mode,
  onMic,
  onSparkles,
  onShare,
  onHeart,
  onEdit,
  onDelete,
  canEdit = false,
  canDelete = false,
  entryTitle = 'Entry'
}: TimelineSidebarBottomNavProps) => {
  const { t } = useTranslation(['common', 'timeline']);
  const [isActionDrawerOpen, setIsActionDrawerOpen] = useState(false);
  const isMobile = useIsMobile();

  const handleHelp = () => {
    toast.info("Help feature coming soon");
  };

  const handleMic = () => {
    if (onMic) {
      onMic();
    } else {
      toast.info("Voice input feature coming soon");
    }
  };

  const handleSparkles = () => {
    if (onSparkles) {
      onSparkles();
    } else {
      toast.info("AI features coming soon");
    }
  };

  const handleShare = () => {
    if (onShare) {
      onShare();
    } else {
      toast.info("Share feature coming soon");
    }
  };

  const handleHeart = () => {
    if (onHeart) {
      onHeart();
    } else {
      toast.info("Reactions coming soon");
    }
  };

  const handleMoreOptions = () => {
    setIsActionDrawerOpen(true);
  };

  const handleDeleteAction = () => {
    // First close the action drawer
    setIsActionDrawerOpen(false);
    
    // Then trigger delete confirmation after a brief delay
    setTimeout(() => {
      if (onDelete) {
        onDelete();
      }
    }, 150);
  };

  const getMoreActions = (): ActionButton[] => {
    const actions: ActionButton[] = [];

    if (canEdit && onEdit) {
      actions.push({
        text: t('timeline:actions.edit'),
        onClick: onEdit,
        variant: 'default'
      });
    }

    if (canDelete && onDelete) {
      actions.push({
        text: t('timeline:actions.delete'),
        onClick: handleDeleteAction,
        variant: 'destructive'
      });
    }

    // Always available actions
    actions.push({
      text: t('timeline:actions.copyLink'),
      onClick: () => {
        const currentPath = window.location.pathname + window.location.search;
        const productionUrl = `${APP_URL}${currentPath}`;
        navigator.clipboard.writeText(productionUrl);
        toast.success(t('timeline:messages.linkCopied'));
      },
      variant: 'outline'
    });

    return actions;
  };

  if (mode === 'create') {
    return (
      <div className="bg-white/20 backdrop-blur-lg border-t border-white/20 shadow-lg">
        <div className="flex justify-between items-center py-4 px-8">
          <ButtonCircle
            onClick={handleHelp}
            size="small"
            aria-label="Help"
            className="hover:bg-white/20"
          >
            <HelpCircle />
          </ButtonCircle>
          
          <ButtonCircle
            onClick={handleMic}
            size="large"
            aria-label="Voice input"
          >
            <Mic />
          </ButtonCircle>
          
          <ButtonCircle
            onClick={handleSparkles}
            size="small"
            aria-label="AI Features"
            className="hover:bg-white/20"
          >
            <Sparkles />
          </ButtonCircle>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white/20 backdrop-blur-lg border-t border-white/20 shadow-lg">
        <div className="flex justify-between items-center py-4 px-8">
          <ButtonCircle
            onClick={handleShare}
            size="small"
            aria-label="Share"
            className="hover:bg-white/20"
          >
            <Upload />
          </ButtonCircle>
          
          <ButtonCircle
            onClick={handleHeart}
            size="large"
            aria-label="Reactions"
          >
            <Heart />
          </ButtonCircle>
          
          <ButtonCircle
            onClick={handleMoreOptions}
            size="small"
            aria-label="More options"
            className="hover:bg-white/20"
          >
            <MoreHorizontal />
          </ButtonCircle>
        </div>
      </div>

      <CustomDrawer
        isOpen={isActionDrawerOpen}
        onClose={() => setIsActionDrawerOpen(false)}
        title={t('timeline:actions.moreOptions')}
        subtitle={`${t('timeline:actions.actionsFor')} "${entryTitle}"`}
        width={isMobile ? '100%' : '400px'}
        maxHeight="60vh"
        containerSelector=".timeline-sidebar"
        constrainToContainer={true}
        backdropScope="container"
        adaptWidth={true}
        footer={
          <button
            onClick={() => setIsActionDrawerOpen(false)}
            className="w-full h-12 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {t('common:cancel')}
          </button>
        }
      >
        {/* Actions */}
        <div className="p-4 space-y-2">
           {getMoreActions().map((action, index) => (
             <button
               key={index}
               onClick={() => {
                 action.onClick();
                 // Only close drawer for non-delete actions (delete handles its own timing)
                 if (action.variant !== 'destructive') {
                   setIsActionDrawerOpen(false);
                 }
               }}
               disabled={action.disabled}
               className={`
                 w-full flex items-center justify-start h-12 px-4 rounded-lg text-left transition-colors
                 ${action.variant === 'destructive' 
                   ? 'text-red-600 hover:bg-red-50' 
                   : action.variant === 'outline'
                   ? 'text-gray-600 hover:bg-gray-50'
                   : 'text-gray-900 hover:bg-gray-50'
                 }
                 disabled:opacity-50 disabled:cursor-not-allowed
               `}
              >
                {action.text}
              </button>
           ))}
        </div>
      </CustomDrawer>
    </>
  );
};

export default TimelineSidebarBottomNav;